import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Button, Checkbox, Typography } from '@mui/joy';

import { TextInputField } from '../FormElements/TextInputField';

interface AnonymousWelcomeProps {
  onJoin: (name: string) => void;
}

const AnonymousWelcome: React.FC<AnonymousWelcomeProps> = ({ onJoin }) => {
  const { t } = useTranslation();
  const [name, setName] = useState<string>('');
  const [consent, setConsent] = useState(false);

  const join = () => {
    if (!consent || !name) {
      return;
    }
    onJoin(name);
  };
  return (
    <Box
      className='flex flex-col items-center justify-center h-full px-4 sm:px-6'
      sx={{ backgroundColor: 'background.level1' }}
    >
      <Box
        className='w-full max-w-md'
        sx={{
          label: { color: 'text.primary' },
          width: '100%'
        }}
      >
        <Box className='mb-4 sm:mb-5'>
          <Typography
            level='h1'
            className='w-full sm:w-3/4'
            sx={{
              color: 'text.primary',
              fontSize: { xs: '1.5rem', sm: '2rem' },
              lineHeight: 1.2
            }}
          >
            {t('crew_view.anonymous_welcome.title')}
          </Typography>
        </Box>
        <TextInputField
          name='displayName'
          label={t('crew_view.anonymous_welcome.name_label')}
          placeholder={t('crew_view.anonymous_welcome.name_placeholder')}
          value={name}
          classNames='px-3 py-3 sm:px-2 sm:py-2'
          sx={{
            backgroundColor: 'background.surface',
            borderColor: 'divider',
            color: 'text.primary',
            minHeight: { xs: '48px', sm: 'auto' },
            '& input': {
              fontSize: { xs: '16px', sm: 'inherit' }, // Prevent zoom on iOS
              minHeight: { xs: '48px', sm: 'auto' }
            },
            '& input::placeholder': {
              color: 'text.tertiary',
              fontWeight: 400,
              opacity: 1
            }
          }}
          onChange={(e) => setName(e.target.value)}
        />
        <Checkbox
          color='neutral'
          variant='outlined'
          className='my-4 sm:my-5'
          label={t('crew_view.anonymous_welcome.consent_label')}
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          sx={{
            '& .MuiCheckbox-root': {
              minHeight: { xs: '44px', sm: 'auto' }, // Touch-friendly on mobile
              minWidth: { xs: '44px', sm: 'auto' }
            },
            '& .MuiTypography-root': {
              fontSize: { xs: '14px', sm: 'inherit' }
            }
          }}
        />
        <Box>
          <Button
            color='neutral'
            variant='solid'
            fullWidth
            disabled={!consent || !name}
            onClick={join}
            sx={{
              minHeight: { xs: '48px', sm: 'auto' }, // Touch-friendly button height
              fontSize: { xs: '16px', sm: 'inherit' },
              fontWeight: 600,
              '&.Mui-disabled': {
                backgroundColor: 'neutral.outlinedDisabledColor',
                color: 'neutral.solidColor'
              }
            }}
          >
            {t('crew_view.anonymous_welcome.join_button')}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default AnonymousWelcome;
